import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import { captureException } from "@/monitoring";
import { streamStopSchema, type StreamStop } from "@/schemas/tv";

import { TvQueryKeys } from "../query-keys";

import type { AxiosInstance } from "axios";

const STOP_ENDPOINT = "/api/v1/tv/client/streams/stop";
const STREAM_STOP_TIMEOUT_MS = 8_000;

let stopQueue: Promise<void> = Promise.resolve();

function waitForPendingStreamStop(): Promise<void> {
  return stopQueue;
}

function __resetStreamStopQueue() {
  stopQueue = Promise.resolve();
}

function enqueueStreamStop(
  task: () => Promise<StreamStop>,
): Promise<StreamStop> {
  const previousStops = stopQueue;
  const thisStop = previousStops.then(() => task());
  stopQueue = settleForWaiters(thisStop);
  return thisStop;
}

function settleForWaiters(thisStop: Promise<StreamStop>): Promise<void> {
  return thisStop.then(
    () => undefined,
    (error: unknown) => {
      void captureException(error, {
        tags: { component: "tv", op: "stop-stream" },
      });
    },
  );
}

function forgetCachedStreamUrl(channelId: string) {
  queryClient.removeQueries({
    queryKey: TvQueryKeys.channelStream(channelId),
    exact: true,
  });
}

async function postStreamStop(api: AxiosInstance): Promise<StreamStop> {
  const { data } = await api.post(STOP_ENDPOINT, undefined, {
    timeout: STREAM_STOP_TIMEOUT_MS,
  });
  const result = streamStopSchema.parse(data);
  if (!result.success) {
    throw new Error("Stream stop failed: expected true, got false");
  }
  return result;
}

function queueStopForChannel(
  channelId: string,
  api: AxiosInstance,
): Promise<StreamStop> {
  forgetCachedStreamUrl(channelId);
  return enqueueStreamStop(() => postStreamStop(api));
}

function useStopChannelStream() {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const {
    mutate: trackStop,
    mutateAsync: trackStopAsync,
    ...mutation
  } = useMutation({
    mutationFn: (work: Promise<StreamStop>) => work,
  });

  const mutate = useCallback(
    (channelId: string) => {
      trackStop(queueStopForChannel(channelId, apiRef.current));
    },
    [trackStop],
  );

  const mutateAsync = useCallback(
    (channelId: string) => {
      return trackStopAsync(queueStopForChannel(channelId, apiRef.current));
    },
    [trackStopAsync],
  );

  return { ...mutation, mutate, mutateAsync };
}

export {
  STREAM_STOP_TIMEOUT_MS,
  useStopChannelStream,
  waitForPendingStreamStop,
  __resetStreamStopQueue,
};
